import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { ViewType } from 'src/app/core/services/view-api.service';
import { ProjectId } from 'src/app/core/state/project.store';
import { PermissionDtoString } from '../../shared/enums/permission-name.enum';
import { ViewPermission, ViewPermissionModel } from '../../shared/models/view-permission';
import { operations } from './api/generated/ix-api.types';
import { ViewAccessApiService } from './view-access-api.service';

@Injectable({
  providedIn: 'root',
})
export class ViewAccessService {
  readonly #projectResource = '/api/projects/';
  // TODO: Move this state to ViewAccessStore
  readonly #viewPermission$ = new BehaviorSubject<NULLABLE<ViewPermissionModel>>(null);
  viewPermission$ = this.#viewPermission$.asObservable().pipe(filter(Boolean));

  readonly #viewAccessApiService = inject(ViewAccessApiService);
  readonly #http = inject(HttpClient);

  getViewAccess(projectId: number) {
    return this.#viewAccessApiService.getViewAccess({ projectId });
  }

  getHasViewPermission(
    path: operations['getViewPermissions']['parameters']['path'],
    query: operations['getViewPermissions']['parameters']['query'],
    viewPermission: PermissionDtoString,
  ): Observable<boolean> {
    return this.getViewPermissions(path, query).pipe(
      map(perms => !!perms.find(perm => perm.permissionName === viewPermission)?.hasPermission),
    );
  }

  getViewPermissions(
    path: operations['getViewPermissions']['parameters']['path'],
    query: operations['getViewPermissions']['parameters']['query'],
  ) {
    return this.#viewAccessApiService.getViewPermissions(path, query);
  }

  /**
   * @deprecated Superseded by {@link getViewPermissions}.
   * @param projectId
   * @returns
   */
  getViewPermissionsDeprecated(
    projectId: ProjectId,
    view: NonNullable<ViewType>,
    packageId?: number,
  ): Observable<ViewPermissionModel> {
    const viewPermissionsUrl = `${this.#projectResource}${projectId}/viewpermissions`;
    let params: HttpParams = new HttpParams().set('view', view);
    if (packageId) {
      params = params.append('trimPackageId', packageId.toString());
    }

    return this.#http
      .get<ViewPermission[]>(viewPermissionsUrl, { params })
      .pipe(map(permissions => new ViewPermissionModel(permissions)))
      .pipe(tap(viewPermission => this.#viewPermission$.next(viewPermission)));
  }

  hasViewPermission(
    projectId: ProjectId,
    view: NonNullable<ViewType>,
    permissionName: PermissionDtoString,
    packageId?: number,
  ): Observable<boolean> {
    return this.getViewPermissionsDeprecated(projectId, view, packageId).pipe(
      map(({ permissions }) => {
        return !!permissions.find(permission => permission.permissionName === permissionName)?.hasPermission;
      }),
    );
  }

  getPermissionStatus(viewPermissions: ViewPermission[], permissionName: string): boolean {
    let hasPermission = false;
    viewPermissions.forEach(permission => {
      if (permission.permissionName === permissionName) {
        hasPermission = permission.hasPermission;
      }
    });
    return hasPermission;
  }
}
